import type {
	BIAIRequest,
	BIAIResponse,
	BIImpactRequest,
	BIImpactResponse,
	BIRequest,
	BIResponse,
	BIRevenueRequest,
	BIRevenueResponse,
	BIRevision,
	CPIRequestModel,
	CPIResponseModel,
	HTTPValidationError,
	Metric,
	MultipleAlertInfoRequestModel,
	ProductsByEngineModel,
	ProductsByEntityRequestModel,
	ProductsByOptimizationStatusModel,
	RepricingMetricsModel,
	RepricingMetricsRequestModel,
	StatisticsMultipleInterpretabilityRequest,
	StatisticsOptimizationStatisticsRequest,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'
import type { AxiosError } from 'axios'

import { useQuery } from '@tanstack/react-query'
import { pickAll, T } from 'lodash/fp'

import { client } from '@/shared/api/client'

export const biQueries = {
	analytic: (body: BIRequest) => {
		/**
		 * Sort request body to avoid unnecessary requests
		 */
		const bosySorted = pickAll(
			[
				'start_date',
				'end_date',
				'metrics',
				'dimensions',
				'date_aggregation',
				'filters',
				'skip_dates',
				'product_filters',
				'currency',
			],
			body
		)

		return {
			queryKey: ['bi', bosySorted],
			queryFn: () => client.bi.getBi(body),
			gcTime: 5 * 60 * 1000,
			staleTime: 5 * 60 * 1000,
		}
	},
	revenue: (body: BIRevenueRequest) => {
		/**
		 * Sort request body to avoid unnecessary requests
		 */
		const bodySorted = pickAll(
			[
				'start_date',
				'end_date',
				'revenue_start_date',
				'revenue_end_date',
				'metrics',
				'dimensions',
				'date_aggregation',
				'filters',
				'skip_dates',
				'currency',
			],
			body
		)

		return {
			queryKey: ['bi-revenue', bodySorted],
			queryFn: () => client.bi.getBiRevenue(body),
			gcTime: 5 * 60 * 1000,
			staleTime: 5 * 60 * 1000,
		}
	},
	ai: (body: BIAIRequest) => ({
		queryKey: ['bi-ai', body],
		queryFn: () => client.bi.getBiAi(body),
		gcTime: 5 * 60 * 1000,
		staleTime: 5 * 60 * 1000,
	}),
	revision: () => ({
		queryKey: ['bi-revision'],
		queryFn: () => client.bi.biRevision(),
		gcTime: 5 * 60 * 1000,
		staleTime: 5 * 60 * 1000,
	}),
	metrics: () => ({
		queryKey: ['metrics'],
		queryFn: () => client.bi.listMetrics(),
		gcTime: 5 * 60 * 1000,
		staleTime: 5 * 60 * 1000,
	}),
	optimizationsStatistics: (
		params: StatisticsOptimizationStatisticsRequest
	) => ({
		queryKey: ['optimizations-statistics', params],
		queryFn: () => client.bi.getOptimizationsStatistics(params),
	}),
	multipleInterpretabilityStatistics: (
		params: StatisticsMultipleInterpretabilityRequest
	) => ({
		queryKey: ['multiple-interpretability-statistics', params],
		queryFn: () => client.bi.getStatisticsMultipleInterpretability(params),
	}),
	multipleAlertsInfo: (params: MultipleAlertInfoRequestModel) => ({
		queryKey: ['multiple-alerts-info', params],
		queryFn: () => client.bi.getMultipleAlertsInfo(params),
	}),
	cpis: (params: CPIRequestModel) => ({
		queryKey: ['cpi', params],
		queryFn: () => client.bi.getCpi(params),
	}),
}

export const useBIAnalyticQuery = <T = BIResponse>(
	body: BIRequest,
	options: UseQueryOptions<
		BIResponse | null,
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<BIResponse | null, AxiosError<HTTPValidationError>, T>({
		...biQueries.analytic(body),
		...options,
	})

export const useCPIQuery = <T = CPIResponseModel[]>(
	params: CPIRequestModel,
	options: UseQueryOptions<
		CPIResponseModel[],
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<CPIResponseModel[], AxiosError<HTTPValidationError>, T>({
		...biQueries.cpis(params),
		...options,
	})

export const useBIRevenueAnalyticQuery = <T = BIRevenueResponse>(
	body: BIRevenueRequest,
	options: UseQueryOptions<
		BIRevenueResponse,
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<BIRevenueResponse, AxiosError<HTTPValidationError>, T>({
		...biQueries.revenue(body),
		...options,
	})

export const useAiBIAnalyticQuery = <T = BIAIResponse>(
	body: BIAIRequest,
	options: UseQueryOptions<
		BIAIResponse,
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<BIAIResponse, AxiosError<HTTPValidationError>, T>({
		...biQueries.ai(body),
		...options,
	})

export const useBIRevisionQuery = <T = BIRevision | null>(
	options?: UseQueryOptions<BIRevision | null, Error, T>
) =>
	useQuery<BIRevision | null, Error, T>({
		...biQueries.revision(),
		...(options || {}),
	})

export const useMetricsQuery = <T = Metric[]>(
	options?: UseQueryOptions<Metric[], Error, T>
) =>
	useQuery<Metric[], Error, T>({
		...biQueries.metrics(),
		...(options || {}),
	})

export const useProductsCountByEngineQuery = <T = ProductsByEngineModel[]>(
	body: ProductsByEntityRequestModel,
	options?: UseQueryOptions<ProductsByEngineModel[], AxiosError, T>
) =>
	useQuery<ProductsByEngineModel[], AxiosError, T>({
		queryKey: ['products-count-by-engine', body],
		queryFn: () => client.bi.getProductsCountByEngine(body),
		gcTime: 5 * 60 * 1000,
		staleTime: 5 * 60 * 1000,
		...options,
	})

export const useBiFieldsQuery = <T = object[] | null>(
	options?: UseQueryOptions<object[] | null, Error, T>
) =>
	useQuery<object[] | null, Error, T>({
		queryKey: ['bi-fields'],
		queryFn: () => client.bi.biFields(),
		...(options || {}),
	})
export const useBiFieldValuesQuery = <T = string[] | null>(
	biField: string,
	options?: UseQueryOptions<string[] | null, Error, T>
) =>
	useQuery<string[] | null, Error, T>({
		queryKey: ['bi-fields', { biField }],
		queryFn: () => client.bi.biFieldsValues({ biField }),
		...(options || {}),
	})
export const useBiImpactQuery = <T = BIResponse>(
	body: BIImpactRequest,
	options: UseQueryOptions<
		BIImpactResponse | null,
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<BIImpactResponse | null, AxiosError<HTTPValidationError>, T>({
		queryKey: ['bi-impact', body],
		queryFn: () => client.bi.getBiImpact(body),
		...options,
	})
export const useProductsCountByOptimizationStatisticsQuery = <
	T = ProductsByOptimizationStatusModel[] | null,
>(
	body: ProductsByEntityRequestModel,
	options: UseQueryOptions<
		ProductsByOptimizationStatusModel[] | null,
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<
		ProductsByOptimizationStatusModel[] | null,
		AxiosError<HTTPValidationError>,
		T
	>({
		queryKey: ['products-count-by-optimization-status', body],
		queryFn: () => client.bi.getProductsCountByOptimizationStatus(body),
		...options,
	})
export const useProductCountByEngineStatisticsQuery = <
	T = ProductsByEngineModel[] | null,
>(
	body: ProductsByEntityRequestModel,
	options: UseQueryOptions<
		ProductsByEngineModel[] | null,
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<ProductsByEngineModel[] | null, AxiosError<HTTPValidationError>, T>({
		queryKey: ['statistics-products-count-by-engine', body],
		queryFn: () => client.bi.getProductsCountByEngine(body),
		...options,
	})
export const useRepricingMetricsStatisticsQuery = <
	T = RepricingMetricsModel[] | null,
>(
	body: RepricingMetricsRequestModel,
	options: UseQueryOptions<
		RepricingMetricsModel[] | null,
		AxiosError<HTTPValidationError>,
		T
	> = {}
) =>
	useQuery<RepricingMetricsModel[] | null, AxiosError<HTTPValidationError>, T>({
		queryKey: ['statistics-repricing-metrics', body],
		queryFn: () => client.bi.getRepricingMetrics(body),
		...options,
	})
